import { apiService, ApiResponse } from './apiService';

export type HealthStatusLevel = 'operational' | 'warning' | 'critical' | 'unknown';

export interface ComponentHealth {
  name: string;
  status: HealthStatusLevel;
  message: string;
  details?: Record<string, any>;
  last_checked: string;
  response_time_ms?: number;
}

export interface DatabaseHealth extends ComponentHealth {
  connection_pool_size?: number;
  active_connections?: number;
  database_version?: string;
}

export interface Auth0Health extends ComponentHealth {
  management_api_available: boolean;
  token_cache_size?: number;
  last_token_refresh?: string;
}

export interface SystemHealth extends ComponentHealth {
  cpu_usage_percent?: number;
  memory_usage_percent?: number;
  disk_usage_percent?: number;
  uptime_seconds?: number;
}

export interface ServiceHealth extends ComponentHealth {
  cache_hit_rate?: number;
  sync_queue_size?: number;
  last_sync_time?: string;
}

export interface BasicHealthResponse {
  status: HealthStatusLevel;
  service: string;
  version: string;
  timestamp: string;
}

export interface DetailedHealthResponse {
  status: HealthStatusLevel;
  service: string;
  version: string;
  timestamp: string;
  uptime_seconds: number;
  
  // Component health details
  database: DatabaseHealth;
  auth0: Auth0Health;
  system: SystemHealth;
  services: ServiceHealth[];
  
  // Overall metrics
  total_components: number;
  healthy_components: number;
  warning_components: number;
  critical_components: number;
  
  // Performance metrics
  avg_response_time_ms?: number;
  health_check_duration_ms: number;
}

export interface HealthSummary {
  status: HealthStatusLevel;
  message: string;
  timestamp: string;
  components_healthy: number;
  components_total: number;
  critical_issues: string[];
  warning_issues: string[];
}

export interface HealthError {
  error: string;
  message: string;
  timestamp: string;
  component?: string;
}

export interface HealthPollingConfig {
  baseInterval: number;
  maxInterval: number;
  minInterval: number;
  backoffMultiplier: number;
  maxRetries: number;
  adaptiveIntervals: boolean;
}

class HealthService {
  private lastBasicStatus: BasicHealthResponse | null = null;
  private lastDetailedStatus: DetailedHealthResponse | null = null;
  private lastSummary: HealthSummary | null = null;
  private lastCheckTime: number = 0;
  private lastSummaryCheckTime: number = 0;
  private lastDetailedCheckTime: number = 0;
  
  // Adaptive polling configuration
  private pollingConfig: HealthPollingConfig = {
    baseInterval: 30000, // 30 seconds
    maxInterval: 300000, // 5 minutes
    minInterval: 5000,   // 5 seconds
    backoffMultiplier: 1.5,
    maxRetries: 3,
    adaptiveIntervals: true,
  };
  
  // Current polling state
  private currentInterval: number = 30000;
  private failureCount: number = 0;
  private isChecking: boolean = false;
  private lastStatusChange: number = 0;
  
  // Status change listeners
  private statusListeners: Array<(status: HealthStatusLevel) => void> = [];
  
  // Background polling
  private backgroundPolling: boolean = false;
  private pollingTimer: NodeJS.Timeout | null = null;

  /**
   * Configure health polling behavior
   */
  configure(config: Partial<HealthPollingConfig>): void {
    this.pollingConfig = { ...this.pollingConfig, ...config };
    this.currentInterval = this.pollingConfig.baseInterval;
  }

  /**
   * Start background polling
   */
  startBackgroundPolling(): void {
    if (this.backgroundPolling) return;
    
    this.backgroundPolling = true;
    this.scheduleNextCheck();
  }

  /**
   * Stop background polling
   */
  stopBackgroundPolling(): void {
    this.backgroundPolling = false;
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  /**
   * Add status change listener
   */
  addStatusListener(listener: (status: HealthStatusLevel) => void): () => void {
    this.statusListeners.push(listener);
    return () => {
      const index = this.statusListeners.indexOf(listener);
      if (index > -1) {
        this.statusListeners.splice(index, 1);
      }
    };
  }

  /**
   * Calculate adaptive interval based on current status
   */
  private calculateAdaptiveInterval(): number {
    if (!this.pollingConfig.adaptiveIntervals) {
      return this.pollingConfig.baseInterval;
    }

    const currentStatus = this.getLogoStatus();
    const baseInterval = this.pollingConfig.baseInterval;
    
    // Faster polling for critical issues
    if (currentStatus === 'critical') {
      return Math.max(baseInterval / 2, this.pollingConfig.minInterval);
    }
    
    // Slightly faster for warnings
    if (currentStatus === 'warning') {
      return Math.max(baseInterval * 0.8, this.pollingConfig.minInterval);
    }
    
    // Slower polling for operational status
    if (currentStatus === 'operational') {
      return Math.min(baseInterval * 1.5, this.pollingConfig.maxInterval);
    }
    
    return baseInterval;
  }

  /**
   * Calculate backoff interval for failures
   */
  private calculateBackoffInterval(): number {
    const baseInterval = this.pollingConfig.baseInterval;
    const backoffMultiplier = this.pollingConfig.backoffMultiplier;
    const maxInterval = this.pollingConfig.maxInterval;
    
    const backoffInterval = baseInterval * Math.pow(backoffMultiplier, this.failureCount);
    return Math.min(backoffInterval, maxInterval);
  }

  /**
   * Schedule next health check
   */
  private scheduleNextCheck(): void {
    if (!this.backgroundPolling) return;
    
    const interval = this.failureCount > 0 
      ? this.calculateBackoffInterval() 
      : this.calculateAdaptiveInterval();
    
    this.currentInterval = interval;
    
    this.pollingTimer = setTimeout(() => {
      this.backgroundHealthCheck();
    }, interval);
  }

  /**
   * Background health check
   */
  private async backgroundHealthCheck(): Promise<void> {
    if (!this.backgroundPolling) return;
    
    try {
      const previousStatus = this.getLogoStatus();
      await this.getHealthSummary();
      const currentStatus = this.getLogoStatus();
      
      // Check for status changes
      if (previousStatus !== currentStatus) {
        this.lastStatusChange = Date.now();
        this.notifyStatusChange(currentStatus);
      }
      
      // Reset failure count on success
      this.failureCount = 0;
    } catch (error) {
      this.failureCount++;
      console.warn(`Background health check failed (attempt ${this.failureCount}):`, error);
      
      // Stop polling after max retries
      if (this.failureCount >= this.pollingConfig.maxRetries) {
        console.error('Max health check retries exceeded, stopping background polling');
        this.stopBackgroundPolling();
        return;
      }
    }
    
    // Schedule next check
    this.scheduleNextCheck();
  }

  /**
   * Notify status change listeners
   */
  private notifyStatusChange(status: HealthStatusLevel): void {
    this.statusListeners.forEach(listener => {
      try {
        listener(status);
      } catch (error) {
        console.error('Status listener error:', error);
      }
    });
  }

  /**
   * Get basic health status for quick checks
   */
  async getBasicHealth(): Promise<BasicHealthResponse> {
    if (this.isChecking) {
      return this.lastBasicStatus || this.getDefaultBasicHealth();
    }

    // Return cached result if recent
    const now = Date.now();
    const cacheAge = now - this.lastCheckTime;
    const maxCacheAge = Math.min(this.currentInterval, 60000); // Max 1 minute cache
    
    if (this.lastBasicStatus && cacheAge < maxCacheAge) {
      return this.lastBasicStatus;
    }

    this.isChecking = true;

    try {
      const startTime = performance.now();
      const response: ApiResponse<BasicHealthResponse> = await apiService.customRequest('/api/v1/health/');
      const endTime = performance.now();
      
      this.lastBasicStatus = response.data;
      this.lastCheckTime = now;
      this.failureCount = 0; // Reset on success
      
      // Log performance metrics
      const responseTime = endTime - startTime;
      console.debug(`Health check completed in ${responseTime.toFixed(2)}ms`);
      
      return response.data;
    } catch (error) {
      console.error('Basic health check failed:', error);
      this.failureCount++;
      
      const errorStatus: BasicHealthResponse = {
        status: 'critical',
        service: 'ping-feedback',
        version: 'unknown',
        timestamp: new Date().toISOString(),
      };

      this.lastBasicStatus = errorStatus;
      this.lastCheckTime = now;
      return errorStatus;
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Get detailed health status with component information
   */
  async getDetailedHealth(): Promise<DetailedHealthResponse> {
    const now = Date.now();
    const cacheAge = now - this.lastDetailedCheckTime;
    const maxCacheAge = Math.min(this.currentInterval * 2, 120000); // Max 2 minutes cache
    
    // Return cached result if recent
    if (this.lastDetailedStatus && cacheAge < maxCacheAge) {
      return this.lastDetailedStatus;
    }

    try {
      const startTime = performance.now();
      const response: ApiResponse<DetailedHealthResponse> = await apiService.customRequest('/api/v1/health/detailed');
      const endTime = performance.now();
      
      this.lastDetailedStatus = response.data;
      this.lastDetailedCheckTime = now;
      
      // Log performance metrics
      const responseTime = endTime - startTime;
      console.debug(`Detailed health check completed in ${responseTime.toFixed(2)}ms`);
      
      return response.data;
    } catch (error) {
      console.error('Detailed health check failed:', error);
      
      // Return basic structure with error status
      const errorStatus: DetailedHealthResponse = {
        status: 'critical',
        service: 'ping-feedback',
        version: 'unknown',
        timestamp: new Date().toISOString(),
        uptime_seconds: 0,
        database: this.getDefaultComponentHealth('database') as DatabaseHealth,
        auth0: {
          ...this.getDefaultComponentHealth('auth0'),
          management_api_available: false,
        } as Auth0Health,
        system: this.getDefaultComponentHealth('system') as SystemHealth,
        services: [],
        total_components: 3,
        healthy_components: 0,
        warning_components: 0,
        critical_components: 3,
        health_check_duration_ms: 0,
      };

      this.lastDetailedStatus = errorStatus;
      this.lastDetailedCheckTime = now;
      return errorStatus;
    }
  }

  /**
   * Get health summary for logo status integration
   */
  async getHealthSummary(): Promise<HealthSummary> {
    const now = Date.now();
    const cacheAge = now - this.lastSummaryCheckTime;
    const maxCacheAge = Math.min(this.currentInterval, 30000); // Max 30 seconds cache
    
    // Return cached result if recent
    if (this.lastSummary && cacheAge < maxCacheAge) {
      return this.lastSummary;
    }

    try {
      const startTime = performance.now();
      const response: ApiResponse<HealthSummary> = await apiService.customRequest('/api/v1/health/summary');
      const endTime = performance.now();
      
      this.lastSummary = response.data;
      this.lastSummaryCheckTime = now;
      this.failureCount = 0; // Reset on success
      
      // Log performance metrics
      const responseTime = endTime - startTime;
      console.debug(`Health summary check completed in ${responseTime.toFixed(2)}ms`);
      
      return response.data;
    } catch (error) {
      console.error('Health summary check failed:', error);
      this.failureCount++;
      
      const errorSummary: HealthSummary = {
        status: 'critical',
        message: 'Health check service unavailable',
        timestamp: new Date().toISOString(),
        components_healthy: 0,
        components_total: 3,
        critical_issues: ['Backend service unreachable'],
        warning_issues: [],
      };

      this.lastSummary = errorSummary;
      this.lastSummaryCheckTime = now;
      return errorSummary;
    }
  }

  /**
   * Get cached basic status without making a new request
   */
  getCachedBasicStatus(): BasicHealthResponse | null {
    return this.lastBasicStatus;
  }

  /**
   * Get cached detailed status without making a new request
   */
  getCachedDetailedStatus(): DetailedHealthResponse | null {
    return this.lastDetailedStatus;
  }

  /**
   * Get cached health summary without making a new request
   */
  getCachedHealthSummary(): HealthSummary | null {
    return this.lastSummary;
  }

  /**
   * Force a fresh health check
   */
  async forceCheck(): Promise<BasicHealthResponse> {
    this.lastCheckTime = 0;
    this.lastSummaryCheckTime = 0;
    this.lastDetailedCheckTime = 0;
    return this.getBasicHealth();
  }

  /**
   * Set custom check interval
   */
  setCheckInterval(intervalMs: number): void {
    this.pollingConfig.baseInterval = intervalMs;
    this.currentInterval = intervalMs;
  }

  /**
   * Get current polling status
   */
  getPollingStatus(): {
    isActive: boolean;
    currentInterval: number;
    failureCount: number;
    lastStatusChange: number;
    nextCheckIn: number;
  } {
    const nextCheckIn = this.pollingTimer 
      ? this.currentInterval - (Date.now() - this.lastSummaryCheckTime)
      : 0;
    
    return {
      isActive: this.backgroundPolling,
      currentInterval: this.currentInterval,
      failureCount: this.failureCount,
      lastStatusChange: this.lastStatusChange,
      nextCheckIn: Math.max(0, nextCheckIn),
    };
  }

  /**
   * Get logo status based on health summary
   */
  getLogoStatus(): 'operational' | 'warning' | 'critical' | 'unknown' {
    if (!this.lastSummary && !this.lastBasicStatus) return 'unknown';

    // Prefer summary if available, otherwise use basic status
    const status = this.lastSummary?.status || this.lastBasicStatus?.status;
    
    switch (status) {
      case 'operational':
        return 'operational';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'critical';
      default:
        return 'unknown';
    }
  }

  /**
   * Get CSS class for logo status animation
   */
  getLogoStatusClass(): string {
    const status = this.getLogoStatus();
    
    switch (status) {
      case 'operational':
        return 'logo-status-operational';
      case 'warning':
        return 'logo-status-warning';
      case 'critical':
        return 'logo-status-critical';
      default:
        return 'logo-status-unknown';
    }
  }

  /**
   * Get human-readable status message
   */
  getStatusMessage(): string {
    const summary = this.lastSummary;
    const basic = this.lastBasicStatus;
    
    if (summary) {
      return summary.message;
    }
    
    if (basic) {
      switch (basic.status) {
        case 'operational':
          return 'All systems operational';
        case 'warning':
          return 'Some systems experiencing issues';
        case 'critical':
          return 'Critical system issues detected';
        default:
          return 'System status unknown';
      }
    }
    
    return 'Unable to determine system status';
  }

  /**
   * Check if system is healthy enough for normal operation
   */
  isSystemHealthy(): boolean {
    const status = this.getLogoStatus();
    return status === 'operational' || status === 'warning';
  }

  /**
   * Get default basic health response
   */
  private getDefaultBasicHealth(): BasicHealthResponse {
    return {
      status: 'unknown',
      service: 'ping-feedback',
      version: 'unknown',
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Get default component health
   */
  private getDefaultComponentHealth(name: string): ComponentHealth {
    return {
      name,
      status: 'unknown',
      message: 'Component status unknown',
      last_checked: new Date().toISOString(),
    };
  }
}

// Create and export singleton instance
export const healthService = new HealthService(); 